import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { fromJS } from 'immutable';
import validator from 'validator';
import { uniqueId } from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import * as _ from 'lodash';

import injectReducer from 'utils/injectReducer';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import { makeSelectBaggagePersonalEffects, makeSelectBaggagePersonalEffectsErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import { Button } from '../components/Button';
import PageSectionHeader from '../components/PageSectionHeader';
import FlexBox from '../../../components/FlexBox';
import {
  ErrorMessage,
  Validate,
  ValidateGroup,
} from '../../../components/Validation';
import {
  handleFormDataChange,
  nextSlide,
  resetDamagedLostExpenseFormData,
  resetDelayedExpenseFormData,
  resetFormCompensationExpense,
} from './actions';
import DatePicker from '../components/DatePicker/index';
import TimePickerInput from '../../../components/TimePickerInput';
import Textbox from '../components/Textbox';
import TextArea from '../components/TextArea';
import OptionsContainer from '../components/OptionsContainer';
import { CLAIMING_FOR, REPORT_TO } from './constants';
import { AdditionalList } from './components/Additional';
import { required } from '../../../utils/validation/validator';
import { saveFormData } from '../actions';
import groupOption from '../../../components/Validation/groupOption';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import AsyncButton from '../../../components/AsyncButton/index';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import HelpText from '../../../components/HelpText';
import { stepPersonalItemsExpenseLookup } from '../../FastCover/dataMapper/claimCodeMapper';
import CurrencySelector from '../components/CurrencySelector';
import { baggagePersonalEffectsLookup } from '../../FastCover/dataMapper/claimCodeMapper';

export class BaggagePersonalEffects extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onChange = (propertyName) => (event) => {
    if (event === null) {
      this.props.onFormDataChange(propertyName, null);
      return;
    }

    if (event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      if (target.type === 'radio') {
        this.props.adjust();
      }
      this.props.onFormDataChange(propertyName, value);
    }
    // this.props.makeDirty(6);
  };

  onAdditionalItemsChange = (data) => {
    this.props.onFormDataChange('form.additionalItems', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onDelayedLuggageExpensesChange = (data) => {
    this.props.onFormDataChange('form.delayedLuggageExpenses', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onLostItemExpensesChange = (data) => {
    this.props.onFormDataChange('form.expenseItemsLostItem', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onValuableExpensesChange = (data) => {
    this.props.onFormDataChange('form.expenseItemsValuables', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onPerDayExpensesChange = (data) => {
    this.props.onFormDataChange('form.expenseItemsPerDay', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onInputComponentChange = (propertyName) => (event) => {
    if (event.target.checked) {
      const selectedValue = this.getSelectedClaiming();
      if (!!selectedValue && selectedValue !== propertyName) {
        this.props.onFormDataChange(`form.${selectedValue}`, 'false');
      }
    }
    this.onChange(`form.${propertyName}`)(event);
    // this.props.makeDirty(6);
  };

  onResize = () => {
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  getSelectedClaiming = () => {
    const {
      claimForPassportCreditCard,
      claimForTheftOfCash,
      claimForLuggageAndPersonalEffect,
    } = this.props.form;

    let selectedValue = null;

    switch ('true') {
      case claimForPassportCreditCard:
        selectedValue = CLAIMING_FOR.PASSPORT_CREDIT_CARD;
        break;
      case claimForTheftOfCash:
        selectedValue = CLAIMING_FOR.THEFT_OF_CASH;
        break;
      case claimForLuggageAndPersonalEffect:
        selectedValue = CLAIMING_FOR.LUGGAGE_AND_PERSONAL_EFFECTS;
        break;
      default:
        selectedValue = null;
        break;
    }

    return selectedValue;
  };

  getValidationErrors() {
    const {
      areYouClaimingForDamaged,
      areYouClaimingForBaggageDelay,
      additionalItems,
      delayedLuggageExpenses,
    } = this.props.form;
    const errors = [];

    if (areYouClaimingForDamaged === 'true') {
      if (this.validateTableField(additionalItems)) {
        errors.push({
          field: 'additionalItems',
          message: 'Expense table fields are mandatory fields.',
        });
      }
    }

    if (areYouClaimingForBaggageDelay === 'true') {
      if (this.validateTableField(delayedLuggageExpenses)) {
        errors.push({
          field: 'delayedLuggageExpenses',
          message: 'Delayed Luggage Expenses table fields are required fields.',
        });
      }
    }

    return errors;
  }

  shouldDisable = (field) => {
    const selectedValue = this.getSelectedClaiming();

    return !!selectedValue && selectedValue !== field;
  };

  appendIds = (array) =>
    array.map((item) => {
      let newItem = item; // eslint-disable-line
      if (!item.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  validateTableField(groups) {
    const invalid = _.some(groups, (fields) =>
      _.some(fields, (value) => {
        const newValue = value ? value.toString() : '';
        return validator.isEmpty(newValue);
      })
    );
    return invalid;
  }

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.props.onFormDataSave();
    this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
  };

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    // const errors = this.validatorGroup.getErrors();
    const valid = this.validatorGroup.checkValidation();

    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }

    this.props.onFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
  };

  handleDayChange = (propertyName) => (day) => {
    const dayWithoutTimeZone = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, dayWithoutTimeZone);
    // this.props.makeDirty(6);
  };

  summaryFormValue() {
    return _.mapValues(this.props.form, (prop) => prop || 'N/A');
  }

  // Are you claiming for Baggage Loss?
  renderClaimingForDamaged() {
    const {
      areYouClaimingForDamaged,
    } = this.props.form;
    return (
      areYouClaimingForDamaged === 'true' &&
      this.renderClaimingForDamagedForm()
    );
  }

  renderClaimingForDamagedForm() {
    const { isSummary, submitted } = this.props;

    const {
      dateOfLossForBaggageInfo,
      placeOfLossForBaggageInfo,
      nameForBaggageInfo,
      cityOrTownForBaggageInfo,
      countryForBaggageInfo,
      postalCodeForBaggageInfo,
      addressForBaggageInfo,
      contactForBaggageInfo,
      phoneForBaggageInfo,
      pleaseDescribeWhatOccurredForBaggageInfo,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;
    return (
      // selectedClaiming &&
      <div className="mb-4 ml-4">
        {this.renderTravelSupplierForDamaged()}
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.lossInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="dateOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={dateOfLossForBaggageInfo}
              fieldName={messages.dateOfLoss}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="dateOfLossForBaggageInfo"
                type="text"
                onDayChange={this.handleDayChange('form.dateOfLossForBaggageInfo')}
                value={dateOfLossForBaggageInfo}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.dateOfLoss}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="placeOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={pleaseDescribeWhatOccurredForBaggageInfo}
              fieldName={messages.pleaseDescribeWhatOccurred}
            >
              <TextArea
                disabled={isReadOnly}
                name="pleaseDescribeWhatOccurred"
                type="text"
                onChange={this.handleChange('form.pleaseDescribeWhatOccurredForBaggageInfo')}
                value={pleaseDescribeWhatOccurredForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.pleaseDescribeWhatOccurred}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="placeOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={placeOfLossForBaggageInfo}
              fieldName={messages.placeOfLoss}
            >
              <Textbox
                disabled={isReadOnly}
                name="placeOfLossForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.placeOfLossForBaggageInfo')}
                value={placeOfLossForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.placeOfLoss}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="nameForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={nameForBaggageInfo}
              fieldName={messages.name}
            >
              <Textbox
                disabled={isReadOnly}
                name="nameForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.nameForBaggageInfo')}
                value={nameForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.name}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="cityOrTownForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={cityOrTownForBaggageInfo}
              fieldName={messages.cityOrTown}
            >
              <Textbox
                disabled={isReadOnly}
                name="cityOrTownForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.cityOrTownForBaggageInfo')}
                value={cityOrTownForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.cityOrTown}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="countryForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={countryForBaggageInfo}
              fieldName={messages.country}
            >
              <Textbox
                disabled={isReadOnly}
                name="countryForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.countryForBaggageInfo')}
                value={countryForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.country}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="postalCodeForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={postalCodeForBaggageInfo}
              fieldName={messages.postalCode}
            >
              <Textbox
                disabled={isReadOnly}
                name="postalCodeForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.postalCodeForBaggageInfo')}
                value={postalCodeForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.postalCode}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressForBaggageInfo}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="addressForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.addressForBaggageInfo')}
                value={addressForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactForBaggageInfo}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.contactForBaggageInfo')}
                value={contactForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneForBaggageInfo}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.phoneForBaggageInfo')}
                value={phoneForBaggageInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        {this.renderAdditionalItems()}
        {this.renderOtherInsuranceAuthorizationForDamaged()}
      </div>
    );
  }

  renderTravelSupplierForDamaged() {
    const { isSummary, submitted } = this.props;
    const {
      originationForBaggage,
      flightNumberOriginationForBaggage,
      airCarrierOriginationForBaggage,
      destinationForBaggage,
      flightNumberDestinationForBaggage,
      airCarrierDestinationForBaggage,
      nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage,
      scheduledDateOfDepartureForBaggage,
      scheduledDateOfReturnForBaggage,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-8">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage}
              fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
            >
              <Textbox
                disabled={isReadOnly}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                type="text"
                onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage')}
                value={nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="scheduledDateOfDeparture"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={scheduledDateOfDepartureForBaggage}
              fieldName={messages.scheduledDateOfDeparture}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="scheduledDateOfDeparture"
                type="text"
                onDayChange={this.handleDayChange('form.scheduledDateOfDepartureForBaggage')}
                value={scheduledDateOfDepartureForBaggage}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.scheduledDateOfDeparture}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="scheduledDateOfReturn"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={scheduledDateOfReturnForBaggage}
              fieldName={messages.scheduledDateOfReturn}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="scheduledDateOfReturn"
                type="text"
                onDayChange={this.handleDayChange('form.scheduledDateOfReturnForBaggage')}
                value={scheduledDateOfReturnForBaggage}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.scheduledDateOfReturn}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="origination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={originationForBaggage}
              fieldName={messages.origination}
            >
              <Textbox
                disabled={isReadOnly}
                name="origination"
                type="text"
                onChange={this.handleChange('form.originationForBaggage')}
                value={originationForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.origination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberOriginationForBaggage}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberOrigination"
                type="text"
                onChange={this.handleChange('form.flightNumberOriginationForBaggage')}
                value={flightNumberOriginationForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierOriginationForBaggage}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierOrigination"
                type="text"
                onChange={this.handleChange('form.airCarrierOriginationForBaggage')}
                value={airCarrierOriginationForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="destination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={destinationForBaggage}
              fieldName={messages.destination}
            >
              <Textbox
                disabled={isReadOnly}
                name="destination"
                type="text"
                onChange={this.handleChange('form.destinationForBaggage')}
                value={destinationForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.destination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberDestinationForBaggage}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberDestination"
                type="text"
                onChange={this.handleChange('form.flightNumberDestinationForBaggage')}
                value={flightNumberDestinationForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierDestinationForBaggage}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierDestination"
                type="text"
                onChange={this.handleChange('form.airCarrierDestinationForBaggage')}
                value={airCarrierDestinationForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  renderOtherInsuranceAuthorizationForDamaged() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameForBaggage,
      addressForBaggage,
      typeOfPolicyForBaggage,
      policyForBaggage,
      contactForBaggage,
      phoneForBaggage,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="companyNameForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={companyNameForBaggage}
              fieldName={messages.companyName}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.companyNameForBaggage')}
                value={companyNameForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.companyName}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressForBaggage}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.addressForBaggage')}
                value={addressForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="typeOfPolicyForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={typeOfPolicyForBaggage}
              fieldName={messages.typeOfPolicy}
            >
              <Textbox
                disabled={isReadOnly}
                name="typeOfPolicyForBaggage"
                type="text"
                onChange={this.handleChange('form.typeOfPolicyForBaggage')}
                value={typeOfPolicyForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.typeOfPolicy}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="policyForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={policyForBaggage}
              fieldName={messages.policy}
            >
              <Textbox
                disabled={isReadOnly}
                name="policyForBaggage"
                type="text"
                onChange={this.handleChange('form.policyForBaggage')}
                value={policyForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.policy}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactForBaggage}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForBaggage"
                type="text"
                onChange={this.handleChange('form.contactForBaggage')}
                value={contactForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneForBaggage}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForBaggage"
                type="text"
                onChange={this.handleChange('form.phoneForBaggage')}
                value={phoneForBaggage}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  renderAdditionalItems() {
    const { additionalItems } = this.props.form;
    const newAdditionalItems = this.appendIds(additionalItems);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onAdditionalItemsChange}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
        {/* <ErrorMessage for="required">Expense table fields are mandatory fields.</ErrorMessage> */}
        {/* </Validate> */}
      </div>
    );
  }

  // Are you claiming for lost items?
  renderClaimingForALostItem() {
    const {
      areYouClaimingForALostItem,
    } = this.props.form;
    return (
      areYouClaimingForALostItem === 'true' &&
      this.renderClaimingForALostItemForm()
    );
  }

  renderClaimingForALostItemForm() {
    const { isSummary, submitted } = this.props;

    const {
      dateOfLossLostItemInfo,
      placeOfLossLostItemInfo,
      addressLostItemInfo,
      contactLostItemInfo,
      phoneLostItemInfo,
      pleaseDescribeWhatOccurredLostItemInfo,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;
    return (
      // selectedClaiming &&
      <div className="mb-4 ml-4">
        {this.renderTravelSupplierForALostItem()}
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.lossInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="dateOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={dateOfLossLostItemInfo}
              fieldName={messages.dateOfLoss}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="dateOfLossForBaggageInfo"
                type="text"
                onDayChange={this.handleDayChange('form.dateOfLossLostItemInfo')}
                value={dateOfLossLostItemInfo}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.dateOfLoss}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="placeOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={pleaseDescribeWhatOccurredLostItemInfo}
              fieldName={messages.pleaseDescribeWhatOccurred}
            >
              <TextArea
                disabled={isReadOnly}
                name="pleaseDescribeWhatOccurred"
                type="text"
                onChange={this.handleChange('form.pleaseDescribeWhatOccurredLostItemInfo')}
                value={pleaseDescribeWhatOccurredLostItemInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.pleaseDescribeWhatOccurred}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="placeOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={placeOfLossLostItemInfo}
              fieldName={messages.placeOfLoss}
            >
              <Textbox
                disabled={isReadOnly}
                name="placeOfLossForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.placeOfLossLostItemInfo')}
                value={placeOfLossLostItemInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.placeOfLoss}
                />
              </ErrorMessage>
            </Validate>
          </div>

        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressLostItemInfo}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="addressForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.addressLostItemInfo')}
                value={addressLostItemInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactLostItemInfo}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.contactLostItemInfo')}
                value={contactLostItemInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneLostItemInfo}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.phoneLostItemInfo')}
                value={phoneLostItemInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        {this.renderExpenseItemsLostItem()}
        {this.renderOtherInsuranceAuthorizationForLostItem()}
      </div>
    );
  }

  renderTravelSupplierForALostItem() {
    const { isSummary, submitted } = this.props;
    const {
      originationLostItem,
      flightNumberOriginationLostItem,
      airCarrierOriginationLostItem,
      destinationLostItem,
      flightNumberDestinationLostItem,
      airCarrierDestinationLostItem,
      nameOfTourOperatorOrCruiseOrLineOrAirlineLostItem,
      scheduledDateOfDepartureLostItem,
      scheduledDateOfReturnLostItem,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-8">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineLostItem}
              fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
            >
              <Textbox
                disabled={isReadOnly}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                type="text"
                onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineLostItem')}
                value={nameOfTourOperatorOrCruiseOrLineOrAirlineLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="scheduledDateOfDeparture"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={scheduledDateOfDepartureLostItem}
              fieldName={messages.scheduledDateOfDeparture}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="scheduledDateOfDeparture"
                type="text"
                onDayChange={this.handleDayChange('form.scheduledDateOfDepartureLostItem')}
                value={scheduledDateOfDepartureLostItem}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.scheduledDateOfDeparture}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="scheduledDateOfReturn"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={scheduledDateOfReturnLostItem}
              fieldName={messages.scheduledDateOfReturn}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="scheduledDateOfReturn"
                type="text"
                onDayChange={this.handleDayChange('form.scheduledDateOfReturnLostItem')}
                value={scheduledDateOfReturnLostItem}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.scheduledDateOfReturn}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="origination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={originationLostItem}
              fieldName={messages.origination}
            >
              <Textbox
                disabled={isReadOnly}
                name="origination"
                type="text"
                onChange={this.handleChange('form.originationLostItem')}
                value={originationLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.origination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberOriginationLostItem}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberOrigination"
                type="text"
                onChange={this.handleChange('form.flightNumberOriginationLostItem')}
                value={flightNumberOriginationLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierOriginationLostItem}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierOrigination"
                type="text"
                onChange={this.handleChange('form.airCarrierOriginationLostItem')}
                value={airCarrierOriginationLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="destination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={destinationLostItem}
              fieldName={messages.destination}
            >
              <Textbox
                disabled={isReadOnly}
                name="destination"
                type="text"
                onChange={this.handleChange('form.destinationLostItem')}
                value={destinationLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.destination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberDestinationLostItem}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberDestination"
                type="text"
                onChange={this.handleChange('form.flightNumberDestinationLostItem')}
                value={flightNumberDestinationLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierDestinationLostItem}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierDestination"
                type="text"
                onChange={this.handleChange('form.airCarrierDestinationLostItem')}
                value={airCarrierDestinationLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  renderExpenseItemsLostItem() {
    const { expenseItemsLostItem } = this.props.form;
    const newAdditionalItems = this.appendIds(expenseItemsLostItem);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onLostItemExpensesChange}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderOtherInsuranceAuthorizationForLostItem() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameLostItem,
      addressLostItem,
      typeOfPolicyLostItem,
      policyLostItem,
      contactLostItem,
      phoneLostItem,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="companyNameForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={companyNameLostItem}
              fieldName={messages.companyName}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.companyNameLostItem')}
                value={companyNameLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.companyName}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressLostItem}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.addressLostItem')}
                value={addressLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="typeOfPolicyForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={typeOfPolicyLostItem}
              fieldName={messages.typeOfPolicy}
            >
              <Textbox
                disabled={isReadOnly}
                name="typeOfPolicyForBaggage"
                type="text"
                onChange={this.handleChange('form.typeOfPolicyLostItem')}
                value={typeOfPolicyLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.typeOfPolicy}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="policyForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={policyLostItem}
              fieldName={messages.policy}
            >
              <Textbox
                disabled={isReadOnly}
                name="policyForBaggage"
                type="text"
                onChange={this.handleChange('form.policyLostItem')}
                value={policyLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.policy}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactLostItem}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForBaggage"
                type="text"
                onChange={this.handleChange('form.contactLostItem')}
                value={contactLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneLostItem}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForBaggage"
                type="text"
                onChange={this.handleChange('form.phoneLostItem')}
                value={phoneLostItem}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  // Are you claiming for valuables?
  renderClaimingForValuables() {
    const {
      areYouClaimingForValuables,
    } = this.props.form;
    return (
      areYouClaimingForValuables === 'true' &&
      this.renderClaimingForValuablesForm()
    );
  }

  renderClaimingForValuablesForm() {
    const { isSummary, submitted } = this.props;

    const {
      dateOfLossValuablesInfo,
      placeOfLossValuablesInfo,
      addressValuablesInfo,
      contactValuablesInfo,
      phoneValuablesInfo,
      pleaseDescribeWhatOccurredValuablesInfo,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;
    return (
      // selectedClaiming &&
      <div className="mb-4 ml-4">
        {this.renderTravelSupplierForValuables()}
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.lossInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="dateOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={dateOfLossValuablesInfo}
              fieldName={messages.dateOfLoss}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="dateOfLossForBaggageInfo"
                type="text"
                onDayChange={this.handleDayChange('form.dateOfLossValuablesInfo')}
                value={dateOfLossValuablesInfo}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.dateOfLoss}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="placeOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={pleaseDescribeWhatOccurredValuablesInfo}
              fieldName={messages.pleaseDescribeWhatOccurred}
            >
              <TextArea
                disabled={isReadOnly}
                name="pleaseDescribeWhatOccurred"
                type="text"
                onChange={this.handleChange('form.pleaseDescribeWhatOccurredValuablesInfo')}
                value={pleaseDescribeWhatOccurredValuablesInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.pleaseDescribeWhatOccurred}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="placeOfLossForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={placeOfLossValuablesInfo}
              fieldName={messages.placeOfLoss}
            >
              <Textbox
                disabled={isReadOnly}
                name="placeOfLossForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.placeOfLossValuablesInfo')}
                value={placeOfLossValuablesInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.placeOfLoss}
                />
              </ErrorMessage>
            </Validate>
          </div>

        </div>
        <div className={'row'}>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressValuablesInfo}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="addressForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.addressValuablesInfo')}
                value={addressValuablesInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactValuablesInfo}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.contactValuablesInfo')}
                value={contactValuablesInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggageInfo"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneValuablesInfo}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForBaggageInfo"
                type="text"
                onChange={this.handleChange('form.phoneValuablesInfo')}
                value={phoneValuablesInfo}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        {this.renderExpenseItemsValuables()}
        {this.renderOtherInsuranceAuthorizationForValuables()}
      </div>
    );
  }

  renderTravelSupplierForValuables() {
    const { isSummary, submitted } = this.props;
    const {
      originationValuables,
      flightNumberOriginationValuables,
      airCarrierOriginationValuables,
      destinationValuables,
      flightNumberDestinationValuables,
      airCarrierDestinationValuables,
      nameOfTourOperatorOrCruiseOrLineOrAirlineValuables,
      scheduledDateOfDepartureValuables,
      scheduledDateOfReturnValuables,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-8">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineValuables}
              fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
            >
              <Textbox
                disabled={isReadOnly}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                type="text"
                onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineValuables')}
                value={nameOfTourOperatorOrCruiseOrLineOrAirlineValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
          </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="scheduledDateOfDeparture"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={scheduledDateOfDepartureValuables}
              fieldName={messages.scheduledDateOfDeparture}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="scheduledDateOfDeparture"
                type="text"
                onDayChange={this.handleDayChange('form.scheduledDateOfDepartureValuables')}
                value={scheduledDateOfDepartureValuables}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.scheduledDateOfDeparture}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-md-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="scheduledDateOfReturn"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={scheduledDateOfReturnValuables}
              fieldName={messages.scheduledDateOfReturn}
            >
              <DatePicker
                position={'bottom-start'}
                disabled={isReadOnly}
                name="scheduledDateOfReturn"
                type="text"
                onDayChange={this.handleDayChange('form.scheduledDateOfReturnValuables')}
                value={scheduledDateOfReturnValuables}
                placeholder=""
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.scheduledDateOfReturn}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="origination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={originationValuables}
              fieldName={messages.origination}
            >
              <Textbox
                disabled={isReadOnly}
                name="origination"
                type="text"
                onChange={this.handleChange('form.originationValuables')}
                value={originationValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.origination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberOriginationValuables}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberOrigination"
                type="text"
                onChange={this.handleChange('form.flightNumberOriginationValuables')}
                value={flightNumberOriginationValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierOriginationValuables}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierOrigination"
                type="text"
                onChange={this.handleChange('form.airCarrierOriginationValuables')}
                value={airCarrierOriginationValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="destination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={destinationValuables}
              fieldName={messages.destination}
            >
              <Textbox
                disabled={isReadOnly}
                name="destination"
                type="text"
                onChange={this.handleChange('form.destinationValuables')}
                value={destinationValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.destination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberDestinationValuables}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberDestination"
                type="text"
                onChange={this.handleChange('form.flightNumberDestinationValuables')}
                value={flightNumberDestinationValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierDestinationValuables}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierDestination"
                type="text"
                onChange={this.handleChange('form.airCarrierDestinationValuables')}
                value={airCarrierDestinationValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  renderExpenseItemsValuables() {
    const { expenseItemsValuables } = this.props.form;
    const newAdditionalItems = this.appendIds(expenseItemsValuables);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onValuableExpensesChange}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderOtherInsuranceAuthorizationForValuables() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameValuables,
      addressValuables,
      typeOfPolicyValuables,
      policyValuables,
      contactValuables,
      phoneValuables,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="companyNameForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={companyNameValuables}
              fieldName={messages.companyName}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.companyNameValuables')}
                value={companyNameValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.companyName}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressValuables}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.addressValuables')}
                value={addressValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="typeOfPolicyForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={typeOfPolicyValuables}
              fieldName={messages.typeOfPolicy}
            >
              <Textbox
                disabled={isReadOnly}
                name="typeOfPolicyForBaggage"
                type="text"
                onChange={this.handleChange('form.typeOfPolicyValuables')}
                value={typeOfPolicyValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.typeOfPolicy}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="policyForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={policyValuables}
              fieldName={messages.policy}
            >
              <Textbox
                disabled={isReadOnly}
                name="policyForBaggage"
                type="text"
                onChange={this.handleChange('form.policyValuables')}
                value={policyValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.policy}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactValuables}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForBaggage"
                type="text"
                onChange={this.handleChange('form.contactValuables')}
                value={contactValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneValuables}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForBaggage"
                type="text"
                onChange={this.handleChange('form.phoneValuables')}
                value={phoneValuables}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  // Are you claiming for Baggage Delay?
  renderDelayLuggageForm() {
    const { isSummary, submitted } = this.props;
    const {
      areYouClaimingForBaggageDelay,
      dateLuggageDelay,
      timeLuggageDelay,
      dateLuggageReturn,
      timeLuggageReturn,
      haveYouMadeAClaimWithYourTravelProvider,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    const newTimeLuggageDelayed = timeLuggageDelay
      ? moment(timeLuggageDelay)
      : null;
    const newTimeLuggageReturned = timeLuggageReturn
      ? moment(timeLuggageReturn)
      : null;

    return (
      areYouClaimingForBaggageDelay === 'true' && (
        <div className="mb-4 ml-4">
          {this.renderTravelSupplierForDelay()}
          <div className="row">
            <PageSectionHeader>
              <FormattedMessage {...messages.delayInformation} />
            </PageSectionHeader>
          </div>
          <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateLuggageDelay"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateLuggageDelay}
                fieldName={messages.dateWhenYourLuggageWasDelayed}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="dateLuggageDelay"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateLuggageDelay')}
                  value={dateLuggageDelay}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateWhenYourLuggageWasDelayed}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="timeLuggageDelay"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={newTimeLuggageDelayed}
                fieldName={messages.timeWhenYourLuggageWasDelayed}
              >
                <TimePickerInput
                  allowEmpty
                  showSecond={false}
                  onChange={this.onChange('form.timeLuggageDelay')}
                  value={newTimeLuggageDelayed}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.timeWhenYourLuggageWasDelayed}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
          <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateLuggageReturn"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateLuggageReturn}
                fieldName={messages.dateWhenYourLuggageWasReturned}
              >
                <DatePicker
                  name="dateLuggageReturn"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateLuggageReturn')}
                  value={dateLuggageReturn}
                  placeholder=""
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateWhenYourLuggageWasReturned}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="timeLuggageReturn"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={newTimeLuggageReturned}
                fieldName={messages.timeWhenYourLuggageWasReturned}
              >
                <TimePickerInput
                  allowEmpty
                  showSecond={false}
                  onChange={this.onChange('form.timeLuggageReturn')}
                  value={newTimeLuggageReturned}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.timeWhenYourLuggageWasReturned}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
          <div className="row">
            <div className="form-group col-sm-6">
              <PageSectionHeader>
                <FormattedMessage {...messages.haveYouMadeAClaimWithYourTravelProvider} />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={haveYouMadeAClaimWithYourTravelProvider === 'true'}
                    onChange={this.onChange('form.haveYouMadeAClaimWithYourTravelProvider')}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={haveYouMadeAClaimWithYourTravelProvider === 'false'}
                    onChange={this.onChange('form.haveYouMadeAClaimWithYourTravelProvider')}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
          {this.renderDelayedLuggageExpenseItems()}
          {this.renderOtherInsuranceAuthorizationForBaggageDelay()}
        </div>
      )
    );
  }

  renderOtherInsuranceAuthorizationForBaggageDelay() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameForDelay,
      addressForDelay,
      cityOrTownForDelay,
      countryForDelay,
      postalCodeForDelay,
      typeOfPolicyForDelay,
      policyForDelay,
      contactForDelay,
      phoneForDelay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="companyNameForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={companyNameForDelay}
              fieldName={messages.companyName}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForDelay"
                type="text"
                onChange={this.handleChange('form.companyNameForDelay')}
                value={companyNameForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.companyName}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressForDelay}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="addressForDelay"
                type="text"
                onChange={this.handleChange('form.addressForDelay')}
                value={addressForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="cityOrTownForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={cityOrTownForDelay}
              fieldName={messages.cityOrTown}
            >
              <Textbox
                disabled={isReadOnly}
                name="cityOrTownForDelay"
                type="text"
                onChange={this.handleChange('form.cityOrTownForDelay')}
                value={cityOrTownForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.cityOrTown}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={countryForDelay}
              fieldName={messages.country}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.countryForDelay')}
                value={countryForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.country}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={postalCodeForDelay}
              fieldName={messages.postalCode}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForBaggage"
                type="text"
                onChange={this.handleChange('form.postalCodeForDelay')}
                value={postalCodeForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.postalCode}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="typeOfPolicyForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={typeOfPolicyForDelay}
              fieldName={messages.typeOfPolicy}
            >
              <Textbox
                disabled={isReadOnly}
                name="typeOfPolicyForDelay"
                type="text"
                onChange={this.handleChange('form.typeOfPolicyForDelay')}
                value={typeOfPolicyForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.typeOfPolicy}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="policyForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={policyForDelay}
              fieldName={messages.policy}
            >
              <Textbox
                disabled={isReadOnly}
                name="policyForDelay"
                type="text"
                onChange={this.handleChange('form.policyForDelay')}
                value={policyForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.policy}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactForDelay}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForDelay"
                type="text"
                onChange={this.handleChange('form.contactForDelay')}
                value={contactForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneForDelay}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForDelay"
                type="text"
                onChange={this.handleChange('form.phoneForDelay')}
                value={phoneForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  renderDelayedLuggageExpenseItems() {
    const { delayedLuggageExpenses } = this.props.form;
    const newDelayedLuggageExpenses = this.appendIds(delayedLuggageExpenses);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="mb-4">
        <PageSectionHeader>
          <FormattedMessage {...messages.delayedLuggageExpenses} />
        </PageSectionHeader>
        <AdditionalList
          data={newDelayedLuggageExpenses}
          onChange={this.onDelayedLuggageExpensesChange}
          isDelayed
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderTravelSupplierForDelay() {
    const { isSummary, submitted } = this.props;
    const {
      originationForDelay,
      flightNumberOriginationForDelay,
      airCarrierOriginationForDelay,
      destinationForDelay,
      flightNumberDestinationForDelay,
      airCarrierDestinationForDelay,
      nameOfTourOperatorOrCruiseOrLineOrAirlineForDelay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-8">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="nameOfTourOperatorOrCruiseOrLineOrAirlineForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineForDelay}
              fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
            >
              <Textbox
                disabled={isReadOnly}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForDelay"
                type="text"
                onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineForDelay')}
                value={nameOfTourOperatorOrCruiseOrLineOrAirlineForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="origination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={originationForDelay}
              fieldName={messages.origination}
            >
              <Textbox
                disabled={isReadOnly}
                name="origination"
                type="text"
                onChange={this.handleChange('form.originationForDelay')}
                value={originationForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.origination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberOriginationForDelay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberOriginationForDelay}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberOriginationForDelay"
                type="text"
                onChange={this.handleChange('form.flightNumberOriginationForDelay')}
                value={flightNumberOriginationForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierOriginationForDelay}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierOriginationForDelay"
                type="text"
                onChange={this.handleChange('form.airCarrierOriginationForDelay')}
                value={airCarrierOriginationForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="destination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={destinationForDelay}
              fieldName={messages.destination}
            >
              <Textbox
                disabled={isReadOnly}
                name="destination"
                type="text"
                onChange={this.handleChange('form.destinationForDelay')}
                value={destinationForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.destination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberDestinationForDelay}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberDestinationForDelay"
                type="text"
                onChange={this.handleChange('form.flightNumberDestinationForDelay')}
                value={flightNumberDestinationForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierDestinationForDelay}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierDestination"
                type="text"
                onChange={this.handleChange('form.airCarrierDestinationForDelay')}
                value={airCarrierDestinationForDelay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  // Are you claiming for Baggage Delay (Per Day)?
  renderBaggagePerDayForm() {
    const { isSummary, submitted } = this.props;
    const {
      areYouClaimingForBaggageDelayPerDay,
      dateLuggagePerDay,
      timeLuggagePerDay,
      dateLuggageReturnPerDay,
      timeLuggageReturnPerDay,
      haveYouMadeAClaimWithYourTravelProviderForPerDay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    const newtimeLuggagePerDayed = timeLuggagePerDay
      ? moment(timeLuggagePerDay)
      : null;
    const newtimeLuggageReturnPerDayed = timeLuggageReturnPerDay
      ? moment(timeLuggageReturnPerDay)
      : null;

    return (
      areYouClaimingForBaggageDelayPerDay === 'true' && (
        <div className="mb-4 ml-4">
          {this.renderTravelSupplierForPerDay()}
          <div className="row">
            <PageSectionHeader>
              <FormattedMessage {...messages.delayInformation} />
            </PageSectionHeader>
          </div>
          <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateLuggagePerDay"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateLuggagePerDay}
                fieldName={messages.dateWhenYourLuggageWasDelayed}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="dateLuggagePerDay"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateLuggagePerDay')}
                  value={dateLuggagePerDay}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateWhenYourLuggageWasDelayed}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="timeLuggagePerDay"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={newtimeLuggagePerDayed}
                fieldName={messages.timeWhenYourLuggageWasDelayed}
              >
                <TimePickerInput
                  allowEmpty
                  showSecond={false}
                  onChange={this.onChange('form.timeLuggagePerDay')}
                  value={newtimeLuggagePerDayed}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.timeWhenYourLuggageWasDelayed}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
          <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateLuggageReturnPerDay"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateLuggageReturnPerDay}
                fieldName={messages.dateWhenYourLuggageWasReturned}
              >
                <DatePicker
                  name="dateLuggageReturnPerDay"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateLuggageReturnPerDay')}
                  value={dateLuggageReturnPerDay}
                  placeholder=""
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateWhenYourLuggageWasReturned}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="timeLuggageReturnPerDay"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={newtimeLuggageReturnPerDayed}
                fieldName={messages.timeWhenYourLuggageWasReturned}
              >
                <TimePickerInput
                  allowEmpty
                  showSecond={false}
                  onChange={this.onChange('form.timeLuggageReturnPerDay')}
                  value={newtimeLuggageReturnPerDayed}
                  disabled={isReadOnly}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.timeWhenYourLuggageWasReturned}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
          <div className="row">
            <div className="form-group col-sm-6">
              <PageSectionHeader>
                <FormattedMessage {...messages.haveYouMadeAClaimWithYourTravelProvider} />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={haveYouMadeAClaimWithYourTravelProviderForPerDay === 'true'}
                    onChange={this.onChange('form.haveYouMadeAClaimWithYourTravelProviderForPerDay')}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={haveYouMadeAClaimWithYourTravelProviderForPerDay === 'false'}
                    onChange={this.onChange('form.haveYouMadeAClaimWithYourTravelProviderForPerDay')}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
          {haveYouMadeAClaimWithYourTravelProviderForPerDay === 'true' && this.renderBaggagePerDayExpenseItems()}
          {this.renderOtherInsuranceAuthorizationForBaggagePerDay()}
        </div>
      )
    );
  }

  renderOtherInsuranceAuthorizationForBaggagePerDay() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameForPerDay,
      addressForPerDay,
      typeOfPolicyForPerDay,
      policyForPerDay,
      contactForPerDay,
      phoneForPerDay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="companyNameForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={companyNameForPerDay}
              fieldName={messages.companyName}
            >
              <Textbox
                disabled={isReadOnly}
                name="companyNameForPerDay"
                type="text"
                onChange={this.handleChange('form.companyNameForPerDay')}
                value={companyNameForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.companyName}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-12">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="addressForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={addressForPerDay}
              fieldName={messages.address}
            >
              <Textbox
                disabled={isReadOnly}
                name="addressForPerDay"
                type="text"
                onChange={this.handleChange('form.addressForPerDay')}
                value={addressForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.address}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="typeOfPolicyForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={typeOfPolicyForPerDay}
              fieldName={messages.typeOfPolicy}
            >
              <Textbox
                disabled={isReadOnly}
                name="typeOfPolicyForPerDay"
                type="text"
                onChange={this.handleChange('form.typeOfPolicyForPerDay')}
                value={typeOfPolicyForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.typeOfPolicy}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="policyForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={policyForPerDay}
              fieldName={messages.policy}
            >
              <Textbox
                disabled={isReadOnly}
                name="policyForPerDay"
                type="text"
                onChange={this.handleChange('form.policyForPerDay')}
                value={policyForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.policy}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="contactForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={contactForPerDay}
              fieldName={messages.contact}
            >
              <Textbox
                disabled={isReadOnly}
                name="contactForPerDay"
                type="text"
                onChange={this.handleChange('form.contactForPerDay')}
                value={contactForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.contact}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-6">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="phoneForBaggage"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={phoneForPerDay}
              fieldName={messages.phone}
            >
              <Textbox
                disabled={isReadOnly}
                name="phoneForPerDay"
                type="text"
                onChange={this.handleChange('form.phoneForPerDay')}
                value={phoneForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.phone}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  renderBaggagePerDayExpenseItems() {
    const { expenseItemsPerDay } = this.props.form;
    const newDelayedLuggageExpenses = this.appendIds(expenseItemsPerDay);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="mb-4">
        <PageSectionHeader>
          <FormattedMessage {...messages.delayedLuggageExpenses} />
        </PageSectionHeader>
        <AdditionalList
          data={newDelayedLuggageExpenses}
          onChange={this.onPerDayExpensesChange}
          isDelayed
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderTravelSupplierForPerDay() {
    const { isSummary, submitted } = this.props;
    const {
      originationForPerDay,
      flightNumberOriginationForPerDay,
      airCarrierOriginationForPerDay,
      destinationForPerDay,
      flightNumberDestinationForPerDay,
      airCarrierDestinationForPerDay,
      nameOfTourOperatorOrCruiseOrLineOrAirlineForPerDay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-sm-8">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="nameOfTourOperatorOrCruiseOrLineOrAirlineForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineForPerDay}
              fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
            >
              <Textbox
                disabled={isReadOnly}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForPerDay"
                type="text"
                onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineForPerDay')}
                value={nameOfTourOperatorOrCruiseOrLineOrAirlineForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="origination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={originationForPerDay}
              fieldName={messages.origination}
            >
              <Textbox
                disabled={isReadOnly}
                name="origination"
                type="text"
                onChange={this.handleChange('form.originationForPerDay')}
                value={originationForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.origination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberOriginationForPerDay"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberOriginationForPerDay}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberOriginationForPerDay"
                type="text"
                onChange={this.handleChange('form.flightNumberOriginationForPerDay')}
                value={flightNumberOriginationForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierOrigination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierOriginationForPerDay}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierOriginationForPerDay"
                type="text"
                onChange={this.handleChange('form.airCarrierOriginationForPerDay')}
                value={airCarrierOriginationForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
        <div className="row">
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="destination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={destinationForPerDay}
              fieldName={messages.destination}
            >
              <Textbox
                disabled={isReadOnly}
                name="destination"
                type="text"
                onChange={this.handleChange('form.destinationForPerDay')}
                value={destinationForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.destination}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="flightNumberDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={flightNumberDestinationForPerDay}
              fieldName={messages.flightNumber}
            >
              <Textbox
                disabled={isReadOnly}
                name="flightNumberDestinationForPerDay"
                type="text"
                onChange={this.handleChange('form.flightNumberDestinationForPerDay')}
                value={flightNumberDestinationForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.flightNumber}
                />
              </ErrorMessage>
            </Validate>
          </div>
          <div className="form-group col-sm-4">
            <Validate
              validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
              name="airCarrierDestination"
              group={this.validatorGroup}
              validators={[required]}
              addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
              validChangeInGroup={this.validatorGroup.validChangeInGroup}
              validationCounter={this.state.validationCounter}
              defaultValue={airCarrierDestinationForPerDay}
              fieldName={messages.airCarrier}
            >
              <Textbox
                disabled={isReadOnly}
                name="airCarrierDestination"
                type="text"
                onChange={this.handleChange('form.airCarrierDestinationForPerDay')}
                value={airCarrierDestinationForPerDay}
              />
              <ErrorMessage for="required">
                <FormattedMessage
                  {...messages.isRequired}
                  values={messages.airCarrier}
                />
              </ErrorMessage>
            </Validate>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      areYouClaimingForDamaged,
      areYouClaimingForALostItem,
      areYouClaimingForValuables,
      areYouClaimingForBaggageDelay,
      areYouClaimingForBaggageDelayPerDay,
    } = this.props.form;
    const { isSummary, submitted, loading } = this.props;

    const isReadOnly = submitted || isSummary;
    const isRenderClaimingForDamaged = !!baggagePersonalEffectsLookup.areYouClaimingForDamaged.claimCode;
    const isRenderClaimingForBaggageDelay = true;
    const isRenderClaimingForALostItem = false;
    const isRenderClaimingForValuables = false;
    const isRenderClaimingForBaggageDelayPerDay = false;
    return (
      <div className="app-content">
        {isRenderClaimingForDamaged &&
          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areYouClaimingForDamaged}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areYouClaimingForDamaged ===
                      'true'
                    }
                    onChange={this.onChange(
                      'form.areYouClaimingForDamaged'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areYouClaimingForDamaged ===
                      'false'
                    }
                    onChange={this.onChange(
                      'form.areYouClaimingForDamaged'
                    )}
                    onClick={this.props.onResetDamagedLostExpense}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
        }
        {isRenderClaimingForDamaged && this.renderClaimingForDamaged()}

        {isRenderClaimingForALostItem &&
          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areYouClaimingForALostItem}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areYouClaimingForALostItem ===
                      'true'
                    }
                    onChange={this.onChange(
                      'form.areYouClaimingForALostItem'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areYouClaimingForALostItem ===
                      'false'
                    }
                    onChange={this.onChange(
                      'form.areYouClaimingForALostItem'
                    )}
                    // onClick={this.props.onResetDamagedLostExpense}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
        }
        {isRenderClaimingForALostItem && this.renderClaimingForALostItem()}

        {isRenderClaimingForValuables &&
          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage
                  {...messages.areYouClaimingForValuables}
                />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areYouClaimingForValuables ===
                      'true'
                    }
                    onChange={this.onChange(
                      'form.areYouClaimingForValuables'
                    )}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={
                      areYouClaimingForValuables ===
                      'false'
                    }
                    onChange={this.onChange(
                      'form.areYouClaimingForValuables'
                    )}
                    // onClick={this.props.onResetDamagedLostExpense}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
        }
        {isRenderClaimingForValuables && this.renderClaimingForValuables()}

        {isRenderClaimingForBaggageDelay &&
          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage {...messages.areYouClaimingForBaggageDelay} />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForBaggageDelay === 'true'}
                    onChange={this.onChange('form.areYouClaimingForBaggageDelay')}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForBaggageDelay === 'false'}
                    onChange={this.onChange('form.areYouClaimingForBaggageDelay')}
                    onClick={this.props.onResetDelayedExpense}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
        }
        {isRenderClaimingForBaggageDelay && this.renderDelayLuggageForm()}

        {isRenderClaimingForBaggageDelayPerDay &&
          <div className="row">
            <div className="form-group col">
              <PageSectionHeader>
                <FormattedMessage {...messages.areYouClaimingForBaggageDelayPerDay} />
              </PageSectionHeader>
              <FlexBox>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForBaggageDelayPerDay === 'true'}
                    onChange={this.onChange('form.areYouClaimingForBaggageDelayPerDay')}
                    value="true"
                  />
                  <FormattedMessage {...messages.yes} />
                </OptionsContainer>
                <OptionsContainer>
                  <input
                    disabled={isReadOnly}
                    type="radio"
                    checked={areYouClaimingForBaggageDelayPerDay === 'false'}
                    onChange={this.onChange('form.areYouClaimingForBaggageDelayPerDay')}
                    // onClick={this.props.onResetDelayedExpense}
                    value="false"
                  />
                  <FormattedMessage {...messages.no} />
                </OptionsContainer>
              </FlexBox>
            </div>
          </div>
        }
        {isRenderClaimingForBaggageDelayPerDay && this.renderBaggagePerDayForm()}

        {!isSummary && (
          <div className={'row justify-content-between'}>
            <div className={'col-12 col-sm-auto'}>
              <div className={'row'}>
                <div className={'col-12 col-sm-auto'}>
                  <button
                    className="btn-outline"
                    onClick={this.props.prev}
                    onKeyDown={this.handleKeyDown}
                  >
                    Back
                  </button>
                </div>
                <div className={'col-12 col-sm-auto'}>
                  <Button
                    onClick={this.handleNext}
                    onKeyDown={this.handleKeyDown}
                    innerRef={(ref) => {
                      this.nextButton = ref;
                    }}
                  >
                    Next &gt;
                  </Button>
                </div>
              </div>
            </div>
            <div className={'col-12 col-sm-auto'}>
              {!submitted && (
                <AsyncButton
                  id={this.saveButtonId}
                  className="btn-outline"
                  loading={loading}
                  spinner={this.showSpinnerForElement(this.saveButtonId)}
                  onClick={this.handleSaveButtonClick}
                  onKeyDown={this.handleKeyDown}
                >
                  Save
                </AsyncButton>
              )}
            </div>
          </div>
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

BaggagePersonalEffects.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  onResetDelayedExpense: PropTypes.func,
  onResetDamagedLostExpense: PropTypes.func,
  loading: PropTypes.bool,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  adjust: PropTypes.func,
  makeValid: PropTypes.func,
  makeError: PropTypes.func,
  onNext: PropTypes.func,
  step: PropTypes.number,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  errors: PropTypes.any,
  onResetCompensationExpense: PropTypes.func,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectBaggagePersonalEffects(),
  errors: makeSelectBaggagePersonalEffectsErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) =>
    dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetDelayedExpense: () => dispatch(resetDelayedExpenseFormData()),
  onResetDamagedLostExpense: () => dispatch(resetDamagedLostExpenseFormData()),
  onResetCompensationExpense: () => dispatch(resetFormCompensationExpense()),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({ key: 'baggagePersonalEffects', reducer });

export default compose(
  withReducer,
  withConnect
)(BaggagePersonalEffects);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Trawick.CFTripCan.BaggagePersonalEffects/index.js